import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Clock, Upload } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/format";
import { PageHeader } from "@/components/Page";

export default function Branding() {
  const { company, refreshBranding } = useAuth();
  const [form, setForm] = useState({ brand_color: "#1E40AF", header_color: "#1E40AF", company_name: "", logo_base64: "" });

  useEffect(() => {
    api.get("/branding").then((r) => {
      if (r.data) setForm((f) => ({ ...f, ...r.data }));
      else setForm((f) => ({ ...f, company_name: company?.name || "" }));
    });
  }, [company]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Logo max. 500 KB");
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      const r = await api.put("/branding", form);
      if (r.data) setForm((f) => ({ ...f, ...r.data }));
      refreshBranding(r.data);
      toast.success("Branding gespeichert");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Firmen-Branding" subtitle="Passe Farben und Logo für dein Portal an." testid="branding-header" />
      <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
        <Card className="p-6 border-slate-200 rounded-xl space-y-5">
          <div className="space-y-2">
            <Label>Firmenname</Label>
            <Input data-testid="branding-name" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Akzentfarbe</Label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid="branding-color" value={form.brand_color || "#1E40AF"} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer" />
                <Input value={form.brand_color || ""} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Header-Farbe</Label>
              <div className="flex items-center gap-2">
                <input type="color" data-testid="branding-header-color" value={form.header_color || "#1E40AF"} onChange={(e) => setForm({ ...form, header_color: e.target.value })} className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer" />
                <Input value={form.header_color || ""} onChange={(e) => setForm({ ...form, header_color: e.target.value })} className="font-mono" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Logo</Label>
            <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-4 py-3 cursor-pointer hover:bg-slate-50 text-sm text-slate-600">
              <Upload className="h-4 w-4" /> Logo hochladen (max. 500 KB)
              <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="branding-logo" />
            </label>
          </div>
          <Button onClick={save} data-testid="branding-save" className="brand-bg text-white hover:opacity-90 w-full">Speichern</Button>
        </Card>

        <Card className="p-6 border-slate-200 rounded-xl" data-testid="branding-preview">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Vorschau</div>
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="h-14 flex items-center gap-3 px-4" style={{ backgroundColor: form.header_color || "#1E40AF" }}>
              {form.logo_base64 ? (
                <img src={form.logo_base64} alt="Logo" className="h-8 w-8 rounded object-cover" />
              ) : (
                <div className="h-8 w-8 rounded bg-white/20 flex items-center justify-center text-white font-bold">{(form.company_name || "T").charAt(0)}</div>
              )}
              <span className="text-white font-semibold">{form.company_name || "Firmenname"}</span>
            </div>
            <div className="p-5 bg-white">
              <div className="h-3 w-32 bg-slate-100 rounded mb-3" />
              <div className="h-3 w-48 bg-slate-100 rounded mb-5" />
              <button className="text-white text-sm font-medium rounded-lg px-4 py-2" style={{ backgroundColor: form.brand_color || "#1E40AF" }}>
                Beispiel-Button
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 text-xs text-slate-400"><Clock className="h-3.5 w-3.5" /> Änderungen erscheinen sofort nach dem Speichern.</div>
        </Card>
      </div>
    </div>
  );
}
